<template>
  <!-- O card inteiro é o alvo: href de verdade para ctrl/meio abrirem a página,
       clique simples sobe como evento para o host abrir o modal. O sortable já
       engole o clique depois de um arrasto, então as duas ações convivem. -->
  <v-card class="kanban-card" variant="flat" border
          :href="href"
          @click="$emit('open', $event)">
    <!-- O identificador é o cabeçalho do card, e não a primeira linha do corpo: numa
         linha só, com reticências, para um identificador longo não empurrar todo o
         resto para baixo. O texto inteiro fica no title. -->
    <div class="card-header">
      <!-- A régua na borda esquerda, e só na altura do CABEÇALHO.

           Deitada no topo ela atravessava o card inteiro e virava a coisa mais forte da coluna;
           de pé de ponta a ponta, uma listra de cor por item. Restrita à faixa do nome, marca o
           card na escala em que ele é lido — e continua sendo a mesma régua das outras telas. -->
      <ontos-color-accent row :color="accent" class="card-accent"/>
      <span class="card-header-title" :title="title">{{ title }}</span>

      <!-- O atalho de mover, SEMPRE à vista. Nasceu para as faixas empilhadas, onde arrastar
           entre raias distantes era sofrido, e por isso só aparecia quando elas deslizavam —
           mas ele nunca foi muleta de tela estreita: é o caminho PRECISO de mover um card,
           contra o arrasto, que é o rápido. Com blocos e dimensões múltiplas no quadro, mirar
           o destino certo arrastando ficou pior em qualquer largura.

           Ele só ABRE a escolha — os valores ficam num modal, que dá conta de qualquer
           distância sem gastar espaço no card. -->
      <!-- Os atalhos do card num grupo próprio, encostados, nesta ordem: mover, editar, e então
           desvincular (num relacionamento da ficha) ou excluir (no quadro da coleção) — pedido do
           usuário. Quem pergunta e grava é o quadro; daqui sobe só o gesto. -->
      <!-- Da tela média para cima, soltos e encostados; no celular, onde o card é estreito e o nome
           precisa do espaço, num menu de ações ⋮, com o nome de cada uma por extenso (escolha do
           usuário). As duas formas desenham a MESMA lista (`actions`). -->
      <template v-if="actions.length">
        <v-menu v-if="$vuetify.display.smAndDown" location="bottom end">
          <template #activator="{ props }">
            <ontos-card-action v-bind="props" icon="mdi-dots-vertical" :title="$tt('options')"/>
          </template>
          <v-list density="compact" min-width="180">
            <v-list-item v-for="action of actions" :key="action.event"
                         :prepend-icon="action.icon" :title="action.title"
                         @click="$emit(action.event, $event)"/>
          </v-list>
        </v-menu>
        <span v-else class="card-actions">
          <ontos-card-action v-for="action of actions" :key="action.event"
                             :icon="action.icon" :title="action.title"
                             @act="$emit(action.event, $event)"/>
        </span>
      </template>
    </div>

    <div v-if="fields.length" class="card-fields">
      <div v-for="property in fields" :key="property.alias"
           class="card-field" :class="{'card-field--stacked': isStacked(property)}">
        <span class="card-field-label" :title="property.name">{{ property.name }}</span>
        <span class="card-field-value">
          <!-- A situação não é propriedade — mora em `_state`, fora do design da coleção —, e
               por isso não passa pelo PropertyValue. Vem pronta de quem a resolve, na bolinha
               que é a forma dela em todo o app. Ver o `stateOptionOf` do tabuleiro. -->
          <ontos-colored-value v-if="property.alias === STATE_KEY && stateOption"
                               :option="stateOption" form="dot"/>
          <template v-else v-for="(value, valueIndex) in valuesOf(property)"
                    :key="`${property.alias}-${valueIndex}`">
            <!-- Separador só entre valores de texto corrido: chip, régua de item e controles
                 de mídia já delimitam o seu — ver needsValueSeparator. -->
            <span v-if="valueIndex > 0 && needsValueSeparator(property)"
                  class="pr-1">&puncsp;|&puncsp;</span>
            <ontos-property-value :property="property"
                                  :value="value"
                                  :item="instance"
                                  :context="context"
                                  :choice-domains="choiceDomains"
                                  variant="card"/>
          </template>
          <ontos-hidden-count :count="hiddenCount(instance, property)"/>
        </span>
      </div>
    </div>
  </v-card>
</template>

<script lang="ts">
import {Component, Vue, Prop, toNative} from "vue-facing-decorator";
import IdeaProperty from "@/types/IdeaProperty";
import {InstanceContext} from "@/types/InstanceContext";
import PropertyValue from "@/components/live/instance/PropertyValue.vue";
import {displayKind, displayValues, hiddenCount, needsValueSeparator} from "@/utils/propertyDisplay";
import HiddenCount from "@/components/shared/HiddenCount.vue";
import Domain from "@/types/Domain";
import ColorAccent from "@/components/shared/ColorAccent.vue";
import CardAction from "@/components/live/instance/CardAction.vue";
import ColoredValue from "@/components/shared/ColoredValue.vue";
import {ColoredOption} from "@/types/ColoredOption";
import {STATE_KEY} from "@/utils/kanbanDimensions";
import {instanceColor} from "@/utils/colors";

/**
 * Card do Kanban. Vive fora do Kanban.vue para que o sandbox de desenvolvimento
 * exercite exatamente este componente, em vez de uma cópia da marcação que
 * envelheceria em silêncio.
 *
 * Não recebe cor nenhuma: a cor de um valor de relacionamento é derivada do id do item, dentro
 * do `PropertyValue`. Chegava como função porque dependia do workspace — qual fluxo pertence à
 * coleção de destino —, e isso deixou de ser verdade quando a cor passou a ser da instância.
 */
@Component({
  emits: ['open', 'pick-state', 'edit', 'unlink', 'delete'],
  components: {
    'ontos-card-action': CardAction,
    'ontos-property-value': PropertyValue,
    'ontos-hidden-count': HiddenCount,
    'ontos-color-accent': ColorAccent,
    'ontos-colored-value': ColoredValue
  }
})
class KanbanCard extends Vue {
  //O mesmo nome da função, para o template chamar sem um método de repasse
  needsValueSeparator = needsValueSeparator
  hiddenCount = hiddenCount
  //Exposta ao template: constante de módulo não é enxergada de lá
  STATE_KEY = STATE_KEY
  /**
   * A cor deste item, para a régua da borda.
   *
   * Derivada do id aqui mesmo, como no chip e na linha da listagem: nenhum host precisa
   * calculá-la nem passá-la — foi justamente a fiação que esta mudança apagou.
   */
  get accent(): string {
    return this.instance && this.instance._instance !== undefined
        ? instanceColor(this.instance._instance) : ''
  }

  @Prop({type: Object, required: true}) readonly instance!: any
  @Prop({type: String, required: true}) readonly title!: string
  @Prop({type: Array as () => IdeaProperty[], default: () => []}) readonly fields!: IdeaProperty[]
  @Prop({type: Object, required: true}) readonly context!: InstanceContext
  /** Os domínios do ambiente, para os valores de seleção — só repassados ao PropertyValue. */
  @Prop({type: Array, default: () => []}) readonly choiceDomains!: Domain[]
  @Prop({type: String, default: ''}) readonly href!: string
/**
   * O rótulo do atalho que move o card, já com o nome da dimensão — "Mudar Situação",
   * "Mudar Prioridade". Vem pronto porque quem sabe qual é a primeira dimensão é o tabuleiro.
   */
  @Prop({type: String, default: ''}) readonly moveLabel!: string
  /**
   * Este card aceita alteração? Sem isto o atalho de mover não é desenhado.
   *
   * Booleana, e não o nível: quem traduz nível em permissão é o tabuleiro, com `can('editor')`.
   * Por prop, e não lendo o ambiente: o card não é componente de rota, e o sandbox o desenha
   * sem ambiente nenhum. Padrão `true` para quem não sabe de permissão continuar servido.
   */
  @Prop({type: Boolean, default: true}) readonly canWrite!: boolean
  /** O card pode ser tirado do dono — só no quadro de um relacionamento da ficha. */
  @Prop({type: Boolean, default: false}) readonly canUnlink!: boolean
  /** O lápis: abre a janela do item já editando. Em todo quadro. */
  @Prop({type: Boolean, default: false}) readonly canEdit!: boolean
  /** A lixeira: exclui o item. Só no quadro da coleção — no de um relacionamento, o lugar é do X. */
  @Prop({type: Boolean, default: false}) readonly canDelete!: boolean

  /**
   * As ações do card, nesta ordem: mover, editar, e então desvincular (num relacionamento da ficha)
   * ou excluir (no quadro da coleção) — pedido do usuário. Cada uma diz o evento que sobe; quem
   * pergunta e grava é o quadro.
   */
  get actions(): { event: string, icon: string, title: string }[] {
    return [
      this.canWrite && {event: 'pick-state', icon: 'mdi-swap-horizontal', title: this.moveLabel},
      this.canEdit && {event: 'edit', icon: 'mdi-pencil', title: this.$tt('edit') as string},
      this.canUnlink
          ? {event: 'unlink', icon: 'mdi-close', title: this.$tt('relationship-unlink') as string}
          : this.canDelete && {event: 'delete', icon: 'mdi-delete', title: this.$tt('delete') as string}
    ].filter((action): action is { event: string, icon: string, title: string } => !!action)
  }
  /**
   * A situação deste item como valor colorido, quando ela está entre os campos escolhidos.
   *
   * Vem PRONTA porque o card não conhece o fluxo: a situação não é propriedade da coleção, e
   * resolver a inicial de quem não tem `_state` gravado é conta de quem tem o fluxo em mão.
   */
  @Prop({type: Object as () => ColoredOption, default: null}) readonly stateOption!: ColoredOption | null

  valuesOf(property: IdeaProperty): any[] {
    return displayValues(this.instance, property)
  }

  /**
   * Rótulo em cima do valor, em vez de ao lado: ao lado de uma caixa de 80px o
   * rótulo fica solto no meio dela. Vale para imagem e não para áudio, que no card
   * é só um ícone e se comporta como qualquer tipo simples.
   *
   * A classificação sai do mesmo displayKind que o PropertyValue usa para escolher o
   * que desenhar — uma segunda lista de tipos aqui sairia de sincronia com ele.
   */
  isStacked(property: IdeaProperty): boolean {
    return displayKind(property) === 'image'
  }
}

export default toNative(KanbanCard)
</script>

<style scoped>
/* Sem a prop `hover` do Vuetify: ela aplica elevação 8, pesada demais para um card
   de 12px de texto. E sem transição, pela mesma razão já registrada no ClassBox — o
   hover acontece durante o arrasto, e animar uma dezena de cards engasga o gesto. */
/* `.v-card` no seletor só para desempatar: a regra `.v-sheet.v-card` do Vuetify fixa
   o raio em 4px com a MESMA especificidade que `.kanban-card` teria, e aí quem vence
   é a ordem de injeção das folhas — que não é garantida entre dev e build. */
.v-card.kanban-card {
  cursor: grab;
  display: block;
  border-radius: 8px;
  border-color: rgba(0, 0, 0, .08);
  box-shadow: 0 1px 2px rgba(0, 0, 0, .06);
}

/* Sem folga em cima e embaixo — aqui a régua É a borda do card, e folga viraria um degrau no
   canto. O raio acompanha o canto do cabeçalho; embaixo ela termina reta, porque continua para
   o corpo do card. Tudo por variável, para não disputar propriedade com o estilo do próprio
   ColorAccent. */
.card-accent {
  --ontos-accent-inset: 0;
  --ontos-accent-width: 3px;
  --ontos-accent-radius: 7px 0 0 0;
}

.kanban-card:hover {
  border-color: rgba(0, 0, 0, .2);
  box-shadow: 0 2px 8px rgba(0, 0, 0, .12);
}

.kanban-card:active {
  cursor: grabbing;
}

/* 13px contra o 1rem do título da faixa: o card fica claramente abaixo dela na
   hierarquia, em qualquer resolução. Antes herdava o 1rem do corpo e os dois
   pesavam igual.

   O raio é repetido aqui em vez de resolvido com overflow: hidden no card: o card é
   arrastado, e o recorte o cortaria no meio do gesto. 7px = o raio do card menos a
   borda de 1px. */
.card-header {
  display: flex;
  align-items: center;
  /* referência de posição para a régua da borda */
  position: relative;
  gap: 2px;
  /* menos à direita: os atalhos têm área de toque própria e já afastam do canto */
  padding: 4px 4px 4px 8px;
  border-radius: 7px 7px 0 0;
  border-bottom: 1px solid rgba(0, 0, 0, .08);
  background: rgba(0, 0, 0, .03);
  font-size: 0.8125rem;
  font-weight: 600;
  line-height: 1.35;
}

/* É o título que cede espaço: os atalhos são estreitos e não podem ser cortados */
.card-header-title {
  flex: 1 1 auto;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Card sem nenhum campo: o cabeçalho é o card inteiro, e aí ele acompanha as quatro
   quinas e não precisa da linha que o separaria de um corpo que não existe. */
.card-header:last-child {
  border-radius: 7px;
  border-bottom: none;
}

/* Os atalhos encostados um no outro: o vão de 2px do cabeçalho os separa do nome, e não entre si.
   Num cabeçalho flex eles não encolhem — quem cede é o nome, com reticências. */
.card-actions {
  display: inline-flex;
  flex: 0 0 auto;
}


/* Um campo por linha, sempre.

   Antes os campos curtos dividiam a linha (flex com quebra, e antes disso uma grade de
   colunas), e a posição de cada atributo dependia do tamanho do valor dos vizinhos: o
   mesmo campo aparecia à direita num card e embaixo no outro, e para achar um atributo
   era preciso ler o card inteiro. Em coluna, o campo fica sempre no mesmo lugar, na ordem
   escolhida no menu de Campos, e cards vizinhos se leem de relance. O preço é altura: um
   card com muitos campos curtos fica mais alto. */
.card-fields {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px;
}

.card-field {
  display: flex;
  align-items: baseline;
  gap: 6px;
  min-width: 0;
  font-size: 12px;
  line-height: 1.3;
}

.card-field-label {
  flex: 0 0 auto;
  max-width: 45%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: rgba(0, 0, 0, .55);
  font-size: 11px;
}

/* Uma linha por valor, sempre: é a quebra de linha que fazia a altura de um card
   depender do tamanho do texto, e cards vizinhos ficarem desiguais dentro da faixa.
   O que não couber vira reticências — o que hoje só acontece com valor maior que a
   linha inteira, já que o campo toma a largura de que precisa. */
.card-field-value {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: rgba(0, 0, 0, .87);
}

/* Rótulo em cima da imagem. stretch e não flex-start: com flex-start os filhos encolhem
   para o conteúdo, e a caixa da imagem não teria a largura do card para se limitar. */
.card-field--stacked {
  flex-direction: column;
  align-items: stretch;
  gap: 2px;
}

/* sem a disputa por espaço horizontal, o rótulo não precisa mais ser cortado */
.card-field--stacked .card-field-label {
  max-width: 100%;
}
</style>
